import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  MessageCircle,
  Phone,
  MapPin,
  Clock,
  Car,
  Mail,
  Facebook,
  Instagram,
  Music,
  Navigation,
  Shield,
  Award
} from 'lucide-react';

const Contact = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-soft overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-6 max-w-4xl mx-auto">
            <h1 className="text-hero text-foreground">Contact & Book Now</h1>
            <p className="text-xl text-primary font-medium">Get in touch to start your wellness transformation</p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Ready to begin your journey to wellness and beauty? Contact us today to book your consultation 
              or ask any questions about our treatments and services.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Get In Touch</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose your preferred way to contact us - we're here to help and respond quickly
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 group">
              <CardContent className="p-8 text-center space-y-6">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant group-hover:scale-110 transition-transform duration-300">
                  <MessageCircle className="h-8 w-8 text-white" />
                </div>
                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-foreground">WhatsApp</h3>
                  <p className="text-muted-foreground">Quick responses & easy booking</p>
                  <p className="text-lg font-semibold text-primary">069 552 3598</p>
                </div>
                <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                  <Button className="gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300 w-full">
                    Message Now
                  </Button>
                </a>
              </CardContent>
            </Card>
            
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 group">
              <CardContent className="p-8 text-center space-y-6">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant group-hover:scale-110 transition-transform duration-300">
                  <Phone className="h-8 w-8 text-white" />
                </div>
                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-foreground">Phone</h3>
                  <p className="text-muted-foreground">Speak directly with our team</p>
                  <p className="text-lg font-semibold text-primary">069 552 3598</p>
                </div>
                <a href="tel:0695523598">
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-smooth w-full">
                    Call Now
                  </Button>
                </a>
              </CardContent>
            </Card>
            
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 group">
              <CardContent className="p-8 text-center space-y-6">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="h-8 w-8 text-white" />
                </div>
                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-foreground">Visit Us</h3>
                  <p className="text-muted-foreground">Come for a consultation</p>
                  <p className="text-sm text-primary font-medium">100 Market Street, Office 12</p>
                </div>
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-smooth w-full">
                  <Navigation className="h-4 w-4 mr-2" />
                  Get Directions
                </Button>
              </CardContent>
            </Card>
            
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 group">
              <CardContent className="p-8 text-center space-y-6">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant group-hover:scale-110 transition-transform duration-300">
                  <Mail className="h-8 w-8 text-white" />
                </div>
                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-foreground">Email</h3>
                  <p className="text-muted-foreground">Send us your questions</p>
                  <p className="text-sm text-primary font-medium">Available via WhatsApp</p>
                </div>
                <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-smooth w-full">
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Location & Hours */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-section text-foreground">Visit Our Studio</h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Located in the heart of Polokwane, our modern wellness studio is easily accessible 
                  with convenient parking and a welcoming atmosphere.
                </p>
              </div>
              
              <div className="space-y-6">
                <Card className="shadow-soft border-0 bg-white">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-6 w-6 text-white" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-foreground">Our Address</h3>
                        <div className="text-muted-foreground">
                          <p>100 Market Street, Office No. 12</p>
                          <p>Amie Bava Square</p>
                          <p>(after Phuti Funerals)</p>
                          <p>Polokwane, South Africa</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="shadow-soft border-0 bg-white">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <Clock className="h-6 w-6 text-white" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-foreground">Opening Hours</h3>
                        <div className="text-muted-foreground space-y-1">
                          <div className="flex justify-between">
                            <span>Monday - Friday:</span>
                            <span className="font-medium">9:00 AM - 5:00 PM</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Saturday:</span>
                            <span className="font-medium">9:00 AM - 1:00 PM</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Sunday & Holidays:</span>
                            <span className="font-medium">Closed</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="shadow-soft border-0 bg-white">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <Car className="h-6 w-6 text-white" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-foreground">Parking</h3>
                        <p className="text-muted-foreground">
                          Secured parking available for your convenience and peace of mind during your visit.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl p-8 shadow-elegant">
                <div className="text-center space-y-6">
                  <div className="w-24 h-24 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant animate-float">
                    <MapPin className="h-12 w-12 text-white" />
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-2xl font-bold text-foreground">Easy to Find</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      We're conveniently located in central Polokwane at Amie Bava Square, 
                      just after Phuti Funerals on Market Street.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-4">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-2 shadow-soft">
                        <Shield className="h-6 w-6 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-foreground">Safe Location</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-2 shadow-soft">
                        <Award className="h-6 w-6 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-foreground">Central Area</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Social Media */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-8 max-w-3xl mx-auto">
            <div className="space-y-4">
              <h2 className="text-section text-foreground">Follow Our Journey</h2>
              <p className="text-lg text-muted-foreground">
                Stay connected with us on social media for wellness tips, client transformations, 
                and special offers.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 group">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                    <Facebook className="h-8 w-8 text-white" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-foreground">Facebook</h3>
                    <p className="text-sm text-muted-foreground">@glowbyprecious</p>
                    <p className="text-xs text-muted-foreground">Updates & community</p>
                  </div>
                  <a href="https://facebook.com/glowbyprecious" target="_blank" rel="noopener noreferrer">
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white w-full">
                      Follow Us
                    </Button>
                  </a>
                </CardContent>
              </Card>
              
              <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 group">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                    <Instagram className="h-8 w-8 text-white" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-foreground">Instagram</h3>
                    <p className="text-sm text-muted-foreground">@glowbyprecious1</p>
                    <p className="text-xs text-muted-foreground">Photos & stories</p>
                  </div>
                  <a href="https://instagram.com/glowbyprecious1" target="_blank" rel="noopener noreferrer">
                    <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white w-full">
                      Follow Us
                    </Button>
                  </a>
                </CardContent>
              </Card>
              
              <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 group">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                    <Music className="h-8 w-8 text-white" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-foreground">TikTok</h3>
                    <p className="text-sm text-muted-foreground">@glowbyprecious</p>
                    <p className="text-xs text-muted-foreground">Tips & transformations</p>
                  </div>
                  <a href="https://tiktok.com/@glowbyprecious" target="_blank" rel="noopener noreferrer">
                    <Button className="bg-black hover:bg-gray-800 text-white w-full">
                      Follow Us
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-600/20 to-purple-600/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h2 className="text-section text-white">Ready to Begin Your Transformation?</h2>
              <p className="text-xl text-pink-100 max-w-2xl mx-auto">
                Don't wait any longer. Contact us today and take the first step towards achieving 
                your wellness and beauty goals.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-primary hover:bg-pink-50 shadow-elegant hover:shadow-lg transition-all duration-300 text-lg px-8 py-4 w-full sm:w-auto">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  WhatsApp Now
                </Button>
              </a>
              <a href="tel:0695523598">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary transition-smooth text-lg px-8 py-4 w-full sm:w-auto">
                  <Phone className="h-5 w-5 mr-2" />
                  Call: 069 552 3598
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;